import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { claimPanelPairing, previewPanelPairing } from '../../../services/panelPairingService';

/**
 * Admin claim flow: enter the pairing code shown on an unprovisioned Touch Panel.
 */
export default function ClaimTouchPanelDialog({ open, onClose, tenantId, onClaimed }) {
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!open) {
      setCode('');
      setPreview(null);
      setError(null);
      setSuccess(null);
      setLoading(false);
      setClaiming(false);
    }
  }, [open]);

  const handlePreview = async () => {
    setError(null);
    setSuccess(null);
    setPreview(null);
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 6) {
      setError('Enter the 6-character pairing code from the panel');
      return;
    }
    setLoading(true);
    try {
      const data = await previewPanelPairing(trimmed);
      setPreview(data);
      setCode(trimmed);
    } catch (e) {
      setError(e.message || 'Could not preview pairing code');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    setError(null);
    setClaiming(true);
    try {
      const data = await claimPanelPairing(code, { tenantId });
      setSuccess(data);
      if (typeof onClaimed === 'function') {
        onClaimed(data);
      }
    } catch (e) {
      setError(e.message || 'Claim failed');
    } finally {
      setClaiming(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Claim Touch Panel</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Enter the pairing code displayed on the panel during Signal Bridge Setup. The panel will
            be registered as an Edge Agent in tenant <strong>{tenantId || '(unknown)'}</strong>.
          </Typography>

          {!success && (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Pairing code"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                inputProps={{ maxLength: 8, style: { letterSpacing: '0.2em', fontFamily: 'monospace' } }}
                fullWidth
                disabled={loading || claiming}
              />
              <Button variant="outlined" onClick={handlePreview} disabled={loading || claiming}>
                {loading ? <CircularProgress size={20} /> : 'Look up'}
              </Button>
            </Stack>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {preview && !success && (
            <Alert severity="info">
              <Typography variant="subtitle2">Confirm device</Typography>
              <Typography variant="body2">Device ID: {preview.deviceId}</Typography>
              <Typography variant="body2">Agent ID: {preview.agentId}</Typography>
              <Typography variant="body2">
                Form factor: {preview.formFactor || 'panel'}{' '}
                <Chip size="small" label={preview.status || 'pending'} sx={{ ml: 1 }} />
              </Typography>
              {preview.appVersion ? (
                <Typography variant="body2">App: {preview.appVersion}</Typography>
              ) : null}
            </Alert>
          )}

          {success && (
            <Alert severity="success">
              Panel claimed as <strong>{success.agentId}</strong>. The device will retrieve
              credentials and come online shortly. You can assign a Touch Panel design next.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{success ? 'Close' : 'Cancel'}</Button>
        {!success && (
          <Button
            variant="contained"
            onClick={handleClaim}
            disabled={!preview || claiming || !tenantId}
          >
            {claiming ? <CircularProgress size={20} /> : 'Confirm claim'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
